import type { CSSProperties } from 'react'
import { useState } from 'react'
import { REACTION_EMOJI, REACTION_MEANING } from '../api/types'
import type { Reaction, ReactionName } from '../api/types'

/** What a person picks from: every reaction but `received`, the receipt an
    agent's listener posts when a tag reaches it. */
export const PICKER_REACTIONS = (Object.keys(REACTION_EMOJI) as ReactionName[])
  .filter((name) => name !== 'received')

/** Whether `me` is among those who gave `reaction`. */
export function holdsReaction(reactions: Reaction[], reaction: ReactionName, me: string): boolean {
  return reactions.some((group) => group.reaction === reaction && group.actors.includes(me))
}

/** A pill, in the accent wash when it is the reader's own. */
function pill(held: boolean): CSSProperties {
  return {
    height: 30, minHeight: 30, padding: '0 10px', borderRadius: 999, fontSize: 14, gap: 6,
    background: held ? 'var(--accent-wash)' : 'var(--panel-strong)',
    border: `1px solid ${held ? 'var(--accent-edge)' : 'var(--border)'}`,
  }
}

/** A face with a plus where its outline breaks: "add one of these", drawn
    rather than spelled, because naming one of the five would claim it is the
    default and there is no default. */
function AddReactionIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8.7 14A6 6 0 1 1 14 8.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.4 9.5a3.1 3.1 0 0 0 4.2 1.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="5.9" cy="6.4" r=".95" fill="currentColor" />
      <circle cx="9.5" cy="6.4" r=".95" fill="currentColor" />
      <path d="M12.7 10.7v3.3M14.35 12.35h-3.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The reactions a message already has, and a pill that adds one of the five.
 *
 * Always shown, never behind hover: a chip is a claim somebody made about the
 * message, and `seen` against `done` on a heads-up is the difference between
 * "noted, I am not in those files" and "I rebased". Hiding that until a pointer
 * arrives would hide the answer, not the affordance.
 *
 * Nothing at all when nobody has reacted: the toolbar, or the long press on a
 * phone, is where a first reaction comes from.
 */
export function Reactions({ reactions, me, onToggle }: {
  reactions: Reaction[]
  /** Whoever is reading, so their own chips can be marked. */
  me: string
  /** `held` says whether the reader already has this one, so the caller knows
      which way to write without asking again. */
  onToggle: (reaction: ReactionName, held: boolean) => void
}) {
  const [picking, setPicking] = useState(false)
  if (!reactions.length) return null

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
      {reactions.map((group) => {
        const held = group.actors.includes(me)
        const title = `${REACTION_MEANING[group.reaction]}\n${group.actors.join(', ')}`
        if (group.reaction === 'received') {
          // A receipt, not a claim anybody can join, so there is nothing to toggle.
          return (
            <span key={group.reaction} title={title} role="img"
                  aria-label={`received by ${group.actors.join(', ')}`}
                  style={{ ...pill(false), display: 'inline-flex', alignItems: 'center' }}>
              <span>{REACTION_EMOJI.received}</span>
              <span style={{ fontFamily: 'var(--font-machine)', fontSize: 12, color: 'var(--text-soft)' }}>{group.count}</span>
            </span>
          )
        }
        return (
          <button
            type="button"
            key={group.reaction}
            aria-pressed={held}
            aria-label={`${group.reaction}, ${group.count}`}
            title={title}
            onClick={() => onToggle(group.reaction, held)}
            className="sp-reaction-chip"
            style={pill(held)}
          >
            <span>{REACTION_EMOJI[group.reaction]}</span>
            <span style={{ fontFamily: 'var(--font-machine)', fontSize: 12, color: held ? 'var(--accent)' : 'var(--text-soft)' }}>{group.count}</span>
          </button>
        )
      })}
      <button
        type="button"
        aria-label="Add a reaction"
        aria-expanded={picking}
        title="Add a reaction"
        onClick={() => setPicking(!picking)}
        className="sp-reaction-chip"
        style={{ ...pill(false), width: 38, justifyContent: 'center', padding: 0, color: 'var(--text-dim)' }}
      >
        <AddReactionIcon />
      </button>
      {picking && PICKER_REACTIONS.map((name) => {
        const held = holdsReaction(reactions, name, me)
        return (
          <button
            type="button"
            key={name}
            aria-label={name}
            aria-pressed={held}
            title={REACTION_MEANING[name]}
            onClick={() => {
              onToggle(name, held)
              setPicking(false)
            }}
            className="sp-reaction-chip"
            style={{ ...pill(held), minWidth: 38, justifyContent: 'center' }}
          >
            {REACTION_EMOJI[name]}
          </button>
        )
      })}
    </div>
  )
}

/**
 * What you can do to a message.
 *
 * On a pointer it floats over the message's top-right corner, revealed by hover
 * or focus, so it costs the room no vertical space and a quiet message stays
 * quiet. Five reactions and a reply, and nothing else: there is no forwarding,
 * no saving and no pinning in this product, and a toolbar that offers only what
 * exists is one nobody has to read twice. With five in the whole set there is
 * no second stage: the set *is* the picker.
 *
 * Only where the pointer can hover. Touch gets the long-press sheet instead
 * (`room/MessageSheet.tsx`).
 */
export function MessageToolbar({ reactions, me, onToggle, onReply }: {
  reactions: Reaction[]
  me: string
  onToggle: (reaction: ReactionName, held: boolean) => void
  /** Absent inside a thread, where the reply box is. */
  onReply?: () => void
}) {
  return (
    <div className="sp-message-toolbar" role="toolbar" aria-label="React or reply">
      {PICKER_REACTIONS.map((name) => {
        const held = holdsReaction(reactions, name, me)
        return (
          <button
            type="button"
            key={name}
            aria-label={name}
            aria-pressed={held}
            title={REACTION_MEANING[name]}
            onClick={() => onToggle(name, held)}
            className="sp-message-toolbar-button"
            style={{ background: held ? 'var(--accent-wash)' : 'transparent' }}
          >
            {REACTION_EMOJI[name]}
          </button>
        )
      })}
      {onReply && (
        <>
          <span style={{ width: 1, alignSelf: 'stretch', margin: '2px 3px', background: 'var(--border)' }} />
          <button
            type="button"
            onClick={onReply}
            className="sp-message-toolbar-button"
            style={{ padding: '3px 8px', font: '600 12px var(--font-interface)', color: 'var(--text-soft)', whiteSpace: 'nowrap' }}
          >
            Reply in thread
          </button>
        </>
      )}
    </div>
  )
}
